import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { sendWelcomeIfNew } from "@/lib/welcome-email.functions";

type Profile = {
  id: string;
  customer_type: "business" | "consumer";
  full_name: string | null;
  phone: string | null;
  verification_status: "pending" | "approved" | "rejected";
  discount_percentage: number | null;
  webshop_enabled: boolean;
};

type AuthCtx = {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  roles: string[];
  isAdmin: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<string[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadProfile = async (uid: string) => {
    const [{ data: p, error: pErr }, { data: rolesData, error: rErr }] = await Promise.all([
      supabase.from("profiles").select("*").eq("id", uid).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", uid),
    ]);
    if (pErr) console.error("[auth] profile load error:", pErr.message);
    if (rErr) console.error("[auth] roles load error:", rErr.message);
    const fetchedRoles = (rolesData ?? []).map((r) => r.role);
    setProfile((p as Profile | null) ?? null);
    setRoles(fetchedRoles);
    setIsAdmin(fetchedRoles.includes("admin"));
  };

  useEffect(() => {
    let roleChannel: ReturnType<typeof supabase.channel> | null = null;

    const subscribeRoles = (uid: string) => {
      if (roleChannel) supabase.removeChannel(roleChannel);
      roleChannel = supabase
        .channel(`user_roles:${uid}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table: "user_roles", filter: `user_id=eq.${uid}` },
          () => {
            loadProfile(uid);
          }
        )
        .subscribe();
    };

    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      if (s?.user) {
        setTimeout(() => {
          loadProfile(s.user.id);
          subscribeRoles(s.user.id);
        }, 0);
        if (event === "SIGNED_IN") {
          // Fire-and-forget; server-side idempotent via welcome_email_sent_at.
          sendWelcomeIfNew({}).catch((err) =>
            console.warn("[auth] welcome email skipped:", err?.message ?? err),
          );
        }
      } else {
        setProfile(null);
        setIsAdmin(false);
        if (roleChannel) {
          supabase.removeChannel(roleChannel);
          roleChannel = null;
        }
      }
    });

    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      if (data.session?.user) {
        loadProfile(data.session.user.id).finally(() => setLoading(false));
        subscribeRoles(data.session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Refresh on focus as a fallback if the realtime socket dropped.
    const onFocus = () => {
      supabase.auth.getSession().then(({ data }) => {
        if (data.session?.user) loadProfile(data.session.user.id);
      });
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      sub.subscription.unsubscribe();
      if (roleChannel) supabase.removeChannel(roleChannel);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const refresh = async () => {
    if (session?.user) await loadProfile(session.user.id);
  };
  const signOut = async () => {
    await supabase.auth.signOut();
    setProfile(null);
    setRoles([]);
    setIsAdmin(false);
  };

  return (
    <Ctx.Provider value={{ session, user: session?.user ?? null, profile, roles, isAdmin, loading, refresh, signOut }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
